import React from 'react';
import { NavLink } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen, Menu, X } from 'lucide-react';

/* El lateral se achica a íconos y se vuelve a abrir al pasar el mouse.
   El estado fijo queda guardado; el de hover no.

   Ojo con una trampa: si la apertura por hover se hace con CSS, al tocar
   "achicar" el puntero queda encima del menú y lo vuelve a abrir en el acto,
   así que parece que el botón no hace nada. Por eso todo se resuelve acá,
   y el hover queda bloqueado hasta que el mouse sale del menú. */

const LLAVE = 'nav_colapsada';
export const ANCHO = 250;
export const ANCHO_CHICO = 72;
export const CELULAR = '(max-width: 768px)';

/* Si estamos en pantalla de celular. Se escucha el cambio de tamaño porque al
   girar el teléfono el menú tiene que pasar de cajón a barra fija sin recargar. */
export function useCelular() {
  const [celular, setCelular] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia(CELULAR).matches);
  React.useEffect(() => {
    const mq = window.matchMedia(CELULAR);
    const ver = (e) => setCelular(e.matches);
    mq.addEventListener('change', ver);
    setCelular(mq.matches);
    return () => mq.removeEventListener('change', ver);
  }, []);
  return celular;
}

/* Mientras el cajón está abierto, lo de atrás no se mueve. Sin esto, en el
   celular se scrollea la página de fondo con el menú abierto y al cerrarlo
   quedás en otro lugar del que estabas. */
export function useTrabarFondo(activo) {
  React.useEffect(() => {
    if (!activo) return undefined;
    const antes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = antes; };
  }, [activo]);
}

/* Escape cierra lo que esté abierto */
export function useEscape(activo, cerrar) {
  React.useEffect(() => {
    if (!activo) return undefined;
    const tecla = (e) => { if (e.key === 'Escape') cerrar(); };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [activo, cerrar]);
}

export function useLateral() {
  const [colapsado, setColapsado] = React.useState(() => {
    try { return localStorage.getItem(LLAVE) === '1'; } catch { return false; }
  });
  const [sobre, setSobre] = React.useState(false);
  const [trabado, setTrabado] = React.useState(false);
  const [cajon, setCajon] = React.useState(false);
  const celular = useCelular();

  const alternar = () => setColapsado(v => {
    try { localStorage.setItem(LLAVE, v ? '0' : '1'); } catch { /* modo privado */ }
    setSobre(false);
    setTrabado(true);       // hasta que el mouse se vaya, el hover no manda
    return !v;
  });

  /* En el celular el panel siempre va entero: achicado a íconos dentro de un
     cajón de 272px se veía una columna de iconitos flotando en el vacío. */
  const abierto = celular ? true : (!colapsado || (sobre && !trabado));

  const cerrar = React.useCallback(() => setCajon(false), []);
  // al volver a pantalla grande no puede quedar un cajón abierto invisible
  React.useEffect(() => { if (!celular) setCajon(false); }, [celular]);
  useTrabarFondo(celular && cajon);
  useEscape(cajon, cerrar);

  // la campana de avisos está anclada a la izquierda: tiene que seguir al menú
  React.useEffect(() => {
    document.documentElement.style.setProperty('--ancho-lateral', (colapsado ? ANCHO_CHICO : ANCHO) + 'px');
  }, [colapsado]);

  return {
    colapsado, abierto, alternar,
    celular, cajon, abrir: () => setCajon(true), cerrar, alternar_cajon: () => setCajon(v => !v),
    props: {
      className: 'barra-lateral' + (colapsado ? ' colapsada' : '') + (cajon ? ' cajon-abierto' : ''),
      style: { width: colapsado ? ANCHO_CHICO : ANCHO },
      onMouseEnter: () => { if (!trabado) setSobre(true); },
      onMouseLeave: () => { setSobre(false); setTrabado(false); },
    },
    /* el ancho real del panel: lo aplica cada layout sobre su <aside> */
    ancho: abierto ? ANCHO : ANCHO_CHICO,
  };
}

/* El fondo atenuado del cajón. Vive siempre en el DOM y se prende con una
   clase: así la animación también corre al cerrar, que con el montaje
   condicional de React no pasaba (desaparecía de golpe). */
export function TapaCajon({ abierto, onClick }) {
  return <div className={'tapa-cajon' + (abierto ? ' visible' : '')} onClick={onClick} aria-hidden="true" />;
}

/* La barra de arriba del celular. El botón del menú vive acá adentro y no
   flotando sobre la pantalla: antes se montaba sobre el título de cada
   página y sobre la franja de superadmin. */
export function BarraMobil({ fondo, abierto, onAlternar, children, derecha }) {
  return (
    <div className="barra-mobil" style={fondo}>
      <button onClick={onAlternar} aria-label={abierto ? 'Cerrar el menú' : 'Abrir el menú'}
        aria-expanded={abierto} className="boton-cajon">
        {abierto ? <X size={22} /> : <Menu size={22} />}
      </button>
      {children}
      {derecha && <div className="barra-mobil-derecha">{derecha}</div>}
    </div>
  );
}

/* Un ítem del menú. Achicado es un cuadrado redondeado con el ícono al medio
   y el nombre como tooltip. */
export function ItemNav({ to, end, icono: Icono, label, abierto, onClick }) {
  return (
    <NavLink to={to} end={end} onClick={onClick} title={abierto ? undefined : label}
      className={({ isActive }) => 'nav-item' + (isActive ? ' activo' : '') + (abierto ? '' : ' solo-icono')}>
      <Icono size={19} />
      {abierto && <span className="nav-texto">{label}</span>}
    </NavLink>
  );
}

export function BotonColapsar({ colapsado, onClick, abierto }) {
  const Icono = colapsado ? PanelLeftOpen : PanelLeftClose;
  return (
    <button onClick={onClick} className={'nav-item accion' + (abierto ? '' : ' solo-icono')}
      title={colapsado ? 'Fijar el menú abierto' : 'Achicar el menú a íconos'}>
      <Icono size={19} />
      {abierto && <span className="nav-texto">{colapsado ? 'Fijar abierto' : 'Achicar menú'}</span>}
    </button>
  );
}

/* Degradé de marca a partir del color del cliente: oscuro arriba, el color
   abajo y un brillo suave al pie. Con un solo color queda plano; la variante
   en la misma escala es la que le da profundidad. */
function aRgb(h) {
  h = String(h || '').replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  const n = parseInt(h, 16);
  return Number.isNaN(n) ? [46, 125, 226] : [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
/* p > 0 aclara, p < 0 oscurece. El alfa va acá adentro: pegarle "55" a un
   rgb(...) da un color inválido y el navegador se come el degradé entero. */
const mezcla = (h, p, alfa = 1) => {
  const t = p < 0 ? 0 : 255, a = Math.abs(p);
  const [r, g, b] = aRgb(h).map(v => Math.round((t - v) * a + v));
  return `rgba(${r},${g},${b},${alfa})`;
};

/* Devuelve color de fondo y degradé por separado a propósito: si el degradé
   fuera inválido, el navegador lo descarta y el panel quedaría transparente,
   dejando ver el contenido de la pantalla a través del menú. Con el color
   sólido abajo, eso no puede pasar. */
export function fondoDeMarca(color) {
  const c = color && String(color).trim() ? (String(color).trim()[0] === '#' ? color : '#' + color) : '#2E7DE2';
  return {
    backgroundColor: mezcla(c, -0.62),
    backgroundImage: [
      `radial-gradient(130% 70% at 12% 102%, ${mezcla(c, 0.34, 0.38)} 0%, transparent 62%)`,
      `radial-gradient(90% 55% at 100% 0%, ${mezcla(c, -0.30, 0.55)} 0%, transparent 70%)`,
      `linear-gradient(162deg, ${mezcla(c, -0.80)} 0%, ${mezcla(c, -0.45)} 52%, ${mezcla(c, 0)} 100%)`,
    ].join(', '),
  };
}

/* Los colores del menú cuando el fondo es el degradé */
export const SOBRE_DEGRADE = {
  '--nav-color': 'rgba(255,255,255,0.80)',
  '--nav-fuerte': '#fff',
  '--nav-hover': 'rgba(255,255,255,0.13)',
  '--nav-activo-fondo': '#fff',
  '--nav-activo-texto': '#0d1b2e',
};
